'use client'

import { Spinner } from '../Progress'
import React, { ReactNode, useId } from 'react'
import { PlacesType, Tooltip, TooltipRefProps } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

export type PopoverWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface PopoverProps
    extends Omit<TooltipRefProps, 'variant' | 'open' | 'close' | 'activeAnchor' | 'isOpen' | 'place'> {
    id?: string
    children?: ReactNode
    popoverContent: ReactNode
    buttons?: ReactNode
    delay?: number
    isLoading?: boolean
    underlined?: boolean
    className?: string
    minWidth?: PopoverWidth
    maxWidth?: PopoverWidth
    place?: PlacesType
}

export const Popover = ({
    id,
    children,
    buttons,
    popoverContent,
    className = '',
    isLoading = false,
    delay = 100,
    underlined = true,
    minWidth = 'sm',
    maxWidth = 'lg',
    place = 'top',
    ...rest
}: PopoverProps): JSX.Element => {
    const reactId = useId()
    const uniqueId = id ?? reactId

    return (
        <div className="popover">
            {children && (
                <div
                    className={`${className} ${underlined && 'underline underline-offset-2 decoration-dotted'}`}
                    data-tooltip-id={uniqueId}
                >
                    {children}
                    <Tooltip
                        {...rest}
                        place={place}
                        id={uniqueId}
                        delayHide={delay}
                        data-tooltip-id={uniqueId}
                        className="bg-shade-100 rounded-lg z-50 !p-0 break-words whitespace-normal !opacity-100"
                        data-cy="popover"
                    >
                        <div
                            className={`pointer-events-auto popover__width-max--${maxWidth} popover__width-min--${minWidth}`}
                        >
                            {isLoading ? (
                                <div className="grid place-content-center">
                                    <Spinner className="m-4" />
                                </div>
                            ) : (
                                <>
                                    <div className="p-4">{popoverContent}</div>
                                    {buttons && <div className="w-full flex justify-between gap-1">{buttons}</div>}
                                </>
                            )}
                        </div>
                    </Tooltip>
                </div>
            )}
        </div>
    )
}
